import { useAccidentsServiceAccidentsRisksList } from '@insurance/api/queries'
import { AccidentRiskCategory } from '@insurance/api/requests'
import { useValidationsField } from '@insurance/hooks'
import { Col, Flex, Form, Row, Select, SelectProps, Switch, Tag } from 'antd'
import { useTranslation } from 'react-i18next'

import { useAccidentInsuranceValues } from './AccidentInsuranceContext'
import { AccidentFormValues } from './type'
import { formProps } from './utils'

type RiskItem = AccidentFormValues['risks'][number]

export const RisksCovered = () => {
  const { t, i18n } = useTranslation()
  const { required } = useValidationsField()
  const { risksCoveredProps, ancillaryRisksCoveredProps } = formProps(t)
  const { isEditable } = useAccidentInsuranceValues()

  const { data, isLoading } = useAccidentsServiceAccidentsRisksList(
    {
      ordering: 'name'
    },
    [i18n.language]
  )

  const isPrimaryOptions =
    data
      ?.filter(
        ({ is_primary, code }) =>
          is_primary && code !== AccidentRiskCategory.ALL_MAIN_RISKS
      )
      ?.map(({ code, name, ...payload }) => ({
        label: name,
        value: code,
        payload
      })) ?? []

  return (
    <Row gutter={[12, 12]}>
      <Col xs={24} xxl={12}>
        <Form.Item dependencies={[ancillaryRisksCoveredProps.name]}>
          {({ getFieldValue, setFieldValue }) => {
            return (
              <Form.Item
                {...risksCoveredProps}
                rules={[required]}
                dependencies={[ancillaryRisksCoveredProps.name]}
              >
                <RisksSelect
                  disabled={isEditable}
                  options={isPrimaryOptions}
                  loading={isLoading}
                  placeholder={risksCoveredProps.label}
                  onChange={(value) => {
                    setFieldValue(risksCoveredProps.name, value)

                    const newPrimaryRiskCodes = value?.map((r) => r.risk) ?? []
                    const currentAncillaryRisks =
                      getFieldValue(ancillaryRisksCoveredProps.name) ?? []

                    if (currentAncillaryRisks.length > 0) {
                      const filteredAncillaryRisks =
                        currentAncillaryRisks.filter(
                          (ancillaryRisk: RiskItem) => {
                            const ancillaryItem = data?.find(
                              ({ code }) => code === ancillaryRisk.risk
                            )

                            if (!ancillaryItem) return false

                            // Backward compatibility: fallback to primary_risk_code if compatible_primary_risks is not available
                            if (
                              !ancillaryItem.compatible_primary_risks &&
                              ancillaryItem.primary_risk_code
                            ) {
                              // Legacy logic: NULL means compatible with all
                              if (!ancillaryItem.primary_risk_code) return true

                              // Otherwise, check if selected primary risk matches
                              return newPrimaryRiskCodes.includes(
                                ancillaryItem.primary_risk_code as AccidentRiskCategory
                              )
                            }

                            if (!ancillaryItem.compatible_primary_risks?.length)
                              return true

                            return ancillaryItem.compatible_primary_risks.some(
                              (compatibleRisk) =>
                                newPrimaryRiskCodes.includes(compatibleRisk)
                            )
                          }
                        )

                      if (
                        filteredAncillaryRisks.length !==
                        currentAncillaryRisks.length
                      ) {
                        setFieldValue(
                          ancillaryRisksCoveredProps.name,
                          filteredAncillaryRisks
                        )
                      }
                    }
                  }}
                />
              </Form.Item>
            )
          }}
        </Form.Item>
      </Col>

      <Col xs={24} xxl={12}>
        <Form.Item dependencies={[risksCoveredProps.name]}>
          {({ getFieldValue }) => {
            const primaryRisks =
              getFieldValue(risksCoveredProps.name)?.map(
                (r: RiskItem) => r.risk
              ) ?? []

            const isAncillaryOptions =
              data
                ?.filter(
                  ({
                    is_primary,
                    compatible_primary_risks,
                    primary_risk_code
                  }) => {
                    // Skip primary risks
                    if (is_primary) return false

                    // Backward compatibility: fallback to primary_risk_code if compatible_primary_risks is not available
                    if (
                      compatible_primary_risks === undefined &&
                      primary_risk_code !== undefined
                    ) {
                      // Legacy logic: NULL means compatible with all
                      if (!primary_risk_code) return true

                      // Otherwise, check if selected primary risk matches
                      return primaryRisks.includes(primary_risk_code)
                    }

                    if (!compatible_primary_risks?.length) return true

                    return compatible_primary_risks.some((compatibleRisk) =>
                      primaryRisks.includes(compatibleRisk)
                    )
                  }
                )
                ?.map(({ code, name, ...payload }) => ({
                  label: name,
                  value: code,
                  payload
                })) ?? []

            return (
              <Form.Item {...ancillaryRisksCoveredProps}>
                <RisksSelect
                  options={isAncillaryOptions}
                  loading={isLoading}
                  placeholder={ancillaryRisksCoveredProps.label}
                  disabled={primaryRisks.length === 0 || isEditable}
                />
              </Form.Item>
            )
          }}
        </Form.Item>
      </Col>
    </Row>
  )
}

const RisksSelect = ({
  value = [],
  onChange,
  ...props
}: SelectProps<RiskItem[] | undefined>) => {
  const { t } = useTranslation()
  const selectedValues = value?.map((r) => r.risk)

  const onSelectChange = (values: AccidentRiskCategory[]) => {
    const updated: RiskItem[] = values.map((code) => {
      const existing = value?.find((risk) => risk.risk === code)

      return existing ?? { risk: code, with_deductible: false }
    })

    onChange?.(updated)
  }

  const onDeductibleToggle = (
    riskCode: AccidentRiskCategory,
    checked: boolean,
    hasValuesSelected: boolean
  ) => {
    const updated = value?.map((risk) =>
      risk.risk === riskCode ? { ...risk, with_deductible: checked } : risk
    )

    if (!hasValuesSelected)
      updated?.push({ risk: riskCode, with_deductible: true })

    onChange?.(updated)
  }

  return (
    <Select
      menuItemSelectedIcon
      mode="multiple"
      value={selectedValues}
      onChange={onSelectChange}
      optionRender={({ label, value: riskCode, data }) => {
        const riskItem = value?.find((r) => r.risk === riskCode)

        if (!data?.payload?.coefficient_deductible) return label

        return (
          <Flex justify="space-between" align="center" gap={8}>
            <span>{label}</span>
            <Flex gap={4} align="center">
              <span>{t('deductible')} - </span>
              <Flex onClick={(e) => e.stopPropagation()}>
                <Switch
                  size="small"
                  checked={riskItem?.with_deductible ?? false}
                  onChange={(checked) =>
                    onDeductibleToggle(
                      riskCode as AccidentRiskCategory,
                      checked,
                      !!riskItem
                    )
                  }
                />
              </Flex>
            </Flex>
          </Flex>
        )
      }}
      tagRender={({ value: riskCode, label, closable, onClose }) => {
        const risk = value?.find((r) => r.risk === riskCode)

        return (
          <Tag
            closable={closable}
            onClose={onClose}
            color={risk?.with_deductible ? 'red' : 'default'}
          >
            {label}

            {risk?.with_deductible
              ? ` - ${t('with_deductible').toLowerCase()}`
              : ''}
          </Tag>
        )
      }}
      {...(props as SelectProps)}
    />
  )
}
